import {
  Box,
  Button,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Content, Header, Page } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { getCompoundEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import { Link as RouterLink } from 'react-router-dom';
import { useMyGroups } from '../../hooks/useMyGroups';

const useStyles = makeStyles(theme => ({
  syncBanner: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.info.light,
    color: theme.palette.getContrastText(theme.palette.info.light),
    border: `1px solid ${theme.palette.info.dark}`,
    '& > :first-child': {
      marginRight: theme.spacing(2),
    },
  },
}));

export function MyGroupsPage() {
  const {
    groups,
    memberOfCount,
    loading,
    syncing,
    syncTimedOut,
    error,
    refresh,
  } = useMyGroups();
  const entityRoute = useRouteRef(entityRouteRef);
  const classes = useStyles();

  const showTopSyncBanner = loading || syncing;
  const showList = groups.length > 0;

  const syncBannerMessage =
    'Still checking groups: scanning the catalog for your direct memberships…';

  return (
    <Page themeId="home">
      <Header
        title="My Groups"
        subtitle="GitLab groups you are a direct member of (parent or child)"
      />
      <Content>
        {showTopSyncBanner && (
          <Box className={classes.syncBanner} role="status" aria-live="polite">
            <CircularProgress size={22} color="inherit" />
            <Typography variant="body2" component="span">
              {syncBannerMessage}
            </Typography>
          </Box>
        )}

        {error && (
          <Typography color="error" paragraph>
            Could not load groups: {error.message}
          </Typography>
        )}

        {!loading &&
          groups.length === 0 &&
          memberOfCount === 0 &&
          !error &&
          !syncing && (
            <Typography variant="body1" color="textSecondary" paragraph>
              {syncTimedOut
                ? 'No direct group memberships are in the catalog yet. Memberships are stored in the catalog database after sync (about every 30 minutes). If you were added to a GitLab group recently, wait for the next sync or click Refresh.'
                : 'No direct group memberships found in the catalog.'}
            </Typography>
          )}

        {!loading && !error && (
          <Box mb={2}>
            <Button variant="outlined" size="small" onClick={refresh}>
              Refresh
            </Button>
          </Box>
        )}

        {syncTimedOut && groups.length === 0 && memberOfCount > 0 && (
          <Typography variant="body2" color="textSecondary" paragraph>
            Catalog sync wait ended; click Refresh if your group list still looks
            incomplete.
          </Typography>
        )}

        {showList && (
          <>
            <Typography variant="body2" color="textSecondary" paragraph>
              {groups.length} group{groups.length === 1 ? '' : 's'} in catalog
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Display name</TableCell>
                    <TableCell>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map(group => {
                    const ref = getCompoundEntityRef(group);
                    const displayName =
                      (group.spec?.profile as { displayName?: string } | undefined)
                        ?.displayName ?? group.metadata.title ?? group.metadata.name;
                    return (
                      <TableRow key={stringifyEntityRef(group)}>
                        <TableCell>
                          <Link
                            component={RouterLink}
                            to={entityRoute(ref)}
                          >
                            {group.metadata.name}
                          </Link>
                        </TableCell>
                        <TableCell>{displayName}</TableCell>
                        <TableCell>
                          {(group.spec?.type as string | undefined) ?? 'team'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Content>
    </Page>
  );
}
